import { useMutation, useQuery } from '@tanstack/react-query';
import { invokeEdgeFunction } from '../../services/edgeFunctions';
import { errorMessage, isRecord } from '../../services/errors';
import { primaryButton } from '../../components/styles';

interface Latest {
  version: string;
  size: number;
}

/** app-update's answer to "latest": the newest released build, or null when none has been published. Throws when it is neither. */
function parseLatest(data: unknown): Latest | null {
  if (!isRecord(data)) throw new Error('the released versions did not arrive');
  if (data.latest === null) return null;
  const latest = data.latest;
  if (!isRecord(latest) || typeof latest.version !== 'string' || typeof latest.size !== 'number') {
    throw new Error('the released versions did not arrive');
  }
  return { version: latest.version, size: latest.size };
}

/**
 * Settings > Android app (CER-217): the newest signed build, for a phone's
 * browser to download and install. It is the same file the app's own updater
 * fetches, so a phone whose app cannot update itself (or cannot sign in) still
 * has a way to the current build. The address it is given lasts minutes and
 * is asked for only when the button is pressed.
 */
export default function AndroidApp() {
  const latest = useQuery({
    queryKey: ['android-app', 'latest'],
    // No installed version to compare with here: the page only wants the newest.
    queryFn: async () => parseLatest(await invokeEdgeFunction('app-update', { action: 'latest', current: '0.0.0' })),
  });
  const download = useMutation({
    mutationFn: async (version: string) => {
      const answer = await invokeEdgeFunction('app-update', { action: 'download', version });
      if (!isRecord(answer) || typeof answer.url !== 'string' || !answer.url.startsWith('https://')) {
        throw new Error('no download address arrived');
      }
      window.location.assign(answer.url);
    },
  });

  if (latest.isPending) return <p className="text-sm text-on-surface-variant">Checking for the newest build…</p>;
  if (latest.isError) return <p className="text-sm text-error">Could not check for the Android app: {errorMessage(latest.error)}</p>;
  if (latest.data === null) return <p className="text-sm text-on-surface-variant">No build has been published here yet.</p>;

  const { version, size } = latest.data;
  return (
    <div className="space-y-3">
      <p className="text-sm text-on-surface-variant">
        Version {version}, {Math.round(size / (1024 * 1024))} MB. Open this page on the phone, download the file and open it: Android
        installs it over the app you have and keeps your data. The first time, Android asks you to allow your browser to install apps.
      </p>
      <button type="button" onClick={() => download.mutate(version)} disabled={download.isPending} className={primaryButton}>
        {download.isPending ? 'Getting the file…' : `Download version ${version}`}
      </button>
      {download.isError && <p className="text-sm text-error">Not downloaded: {errorMessage(download.error)}</p>}
    </div>
  );
}
